/** Opaque timer identifier. */
export type TimerId = string;

interface TimerEntry {
  remaining: number; // seconds
  onExpire: () => void;
}

/**
 * Delta-time countdown clock shared by all engine systems.
 *
 * Callers register a named timer with a duration and a callback; the clock
 * fires the callback once after that many seconds of world-time have elapsed.
 * Because tick() is called from the gated RAF loop, timers automatically
 * pause whenever the world is frozen (dialogue, menu, etc.).
 *
 * Usage:
 *   cooldownClock.register("enemy:atk:goblin-1", 3.0, () => goblinAttack());
 *   cooldownClock.unregister("enemy:atk:goblin-1"); // cancel early
 */
export class CooldownClock {
  private readonly timers = new Map<TimerId, TimerEntry>();

  /**
   * Register a countdown timer.
   * Re-registering an existing id resets it to the new duration.
   */
  register(id: TimerId, durationSec: number, onExpire: () => void): void {
    this.timers.set(id, { remaining: durationSec, onExpire });
  }

  /** Cancel a timer before it fires. No-op if the id is not active. */
  unregister(id: TimerId): void {
    this.timers.delete(id);
  }

  /** True while a timer with this id is counting down. */
  isActive(id: TimerId): boolean {
    return this.timers.has(id);
  }

  /**
   * Seconds left on `id`, or 0 when no timer is active. Used by HUD readouts
   * (e.g. `getPlayerAtbState`) that need the live remaining for a fill bar.
   */
  remaining(id: TimerId): number {
    const entry = this.timers.get(id);
    return entry ? Math.max(0, entry.remaining) : 0;
  }

  /**
   * Advance all active timers by `delta` seconds and fire any that expire.
   * Call this once per frame from the world-gated RAF loop.
   */
  tick(delta: number): void {
    // Snapshot keys so callbacks that re-register don't affect this tick.
    for (const id of [...this.timers.keys()]) {
      const entry = this.timers.get(id);
      if (!entry) continue; // unregistered during this tick
      entry.remaining -= delta;
      if (entry.remaining <= 0) {
        this.timers.delete(id);
        entry.onExpire();
      }
    }
  }
}

/** Singleton shared by all engine modules. */
export const cooldownClock = new CooldownClock();
